import { useState } from 'react'
import { Button, Input, Textarea, Label, Card } from '@creanizer/ui'
import { Plus, Trash2, Edit2, Save, X } from 'lucide-react'
import type { CharacterSampleDialogue } from '@creanizer/types'

interface SampleDialoguesEditorProps {
  dialogues: CharacterSampleDialogue[]
  onChange: (dialogues: CharacterSampleDialogue[]) => void
}

export function SampleDialoguesEditor({
  dialogues,
  onChange
}: SampleDialoguesEditorProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editedDialogue, setEditedDialogue] = useState<CharacterSampleDialogue | null>(null)
  const [isAdding, setIsAdding] = useState(false)
  const [newDialogue, setNewDialogue] = useState<CharacterSampleDialogue>({
    situation: '',
    line: '',
    tone: '',
    category: ''
  })

  const handleEdit = (index: number) => {
    setEditingIndex(index)
    setEditedDialogue({ ...dialogues[index] })
  }

  const handleSaveEdit = () => {
    if (editingIndex !== null && editedDialogue) {
      const newDialogues = [...dialogues]
      newDialogues[editingIndex] = editedDialogue
      onChange(newDialogues)
      setEditingIndex(null)
      setEditedDialogue(null)
    }
  }

  const handleCancelEdit = () => {
    setEditingIndex(null)
    setEditedDialogue(null)
  }

  const handleDelete = (index: number) => {
    if (confirm('この台詞を削除しますか？')) {
      const newDialogues = dialogues.filter((_, i) => i !== index)
      onChange(newDialogues)
    }
  }

  const handleAdd = () => {
    if (newDialogue.situation && newDialogue.line && newDialogue.tone) {
      onChange([...dialogues, newDialogue])
      setNewDialogue({ situation: '', line: '', tone: '', category: '' })
      setIsAdding(false)
    }
  }

  const handleCancelAdd = () => {
    setNewDialogue({ situation: '', line: '', tone: '', category: '' })
    setIsAdding(false)
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Label className="text-lg font-semibold">サンプル台詞</Label>
        {!isAdding && (
          <Button
            size="sm"
            onClick={() => setIsAdding(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            台詞を追加
          </Button>
        )}
      </div>

      {isAdding && (
        <Card className="p-4 border-primary/50">
          <div className="space-y-3">
            <div>
              <Label htmlFor="new-situation">シチュエーション</Label>
              <Input
                id="new-situation"
                value={newDialogue.situation}
                onChange={(e) => setNewDialogue(prev => ({ ...prev, situation: e.target.value }))}
                placeholder="例: 戦闘開始時"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="new-line">台詞</Label>
              <Textarea
                id="new-line"
                value={newDialogue.line}
                onChange={(e) => setNewDialogue(prev => ({ ...prev, line: e.target.value }))}
                placeholder="例: ここが正念場だ！"
                rows={2}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new-tone">口調・感情</Label>
                <Input
                  id="new-tone"
                  value={newDialogue.tone}
                  onChange={(e) => setNewDialogue(prev => ({ ...prev, tone: e.target.value }))}
                  placeholder="例: 決意に満ちた声で"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="new-category">カテゴリ</Label>
                <Input
                  id="new-category"
                  value={newDialogue.category || ''}
                  onChange={(e) => setNewDialogue(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="例: 日常、戦闘、感情的"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd}>
                <Save className="h-4 w-4 mr-2" />
                追加
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancelAdd}>
                <X className="h-4 w-4 mr-2" />
                キャンセル
              </Button>
            </div>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {dialogues.map((dialogue, index) => (
          <Card key={index} className="p-4">
            {editingIndex === index && editedDialogue ? (
              <div className="space-y-3">
                <div>
                  <Label htmlFor={`edit-situation-${index}`}>シチュエーション</Label>
                  <Input
                    id={`edit-situation-${index}`}
                    value={editedDialogue.situation}
                    onChange={(e) => setEditedDialogue(prev => prev ? { ...prev, situation: e.target.value } : null)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor={`edit-line-${index}`}>台詞</Label>
                  <Textarea
                    id={`edit-line-${index}`}
                    value={editedDialogue.line}
                    onChange={(e) => setEditedDialogue(prev => prev ? { ...prev, line: e.target.value } : null)}
                    rows={2}
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`edit-tone-${index}`}>口調・感情</Label>
                    <Input
                      id={`edit-tone-${index}`}
                      value={editedDialogue.tone}
                      onChange={(e) => setEditedDialogue(prev => prev ? { ...prev, tone: e.target.value } : null)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`edit-category-${index}`}>カテゴリ</Label>
                    <Input
                      id={`edit-category-${index}`}
                      value={editedDialogue.category || ''}
                      onChange={(e) => setEditedDialogue(prev => prev ? { ...prev, category: e.target.value } : null)}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSaveEdit}>
                    <Save className="h-4 w-4 mr-2" />
                    保存
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                    <X className="h-4 w-4 mr-2" />
                    キャンセル
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-muted-foreground">
                      シチュエーション: {dialogue.situation}
                    </p>
                    <p className="text-base mt-2 font-medium">
                      「{dialogue.line}」
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {dialogue.tone}
                      {dialogue.category && (
                        <span className="ml-2 px-2 py-0.5 bg-muted rounded text-xs">
                          {dialogue.category}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(index)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>

      {dialogues.length === 0 && !isAdding && (
        <p className="text-center text-muted-foreground py-4">
          サンプル台詞がありません
        </p>
      )}
    </div>
  )
}